import { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { LicenseRequest, Track } from "@shared/schema";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Search } from "lucide-react";

const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  projectType: z.string().min(1, { message: "Please select a project type" }),
  usageDuration: z.string().min(1, { message: "Please select a usage duration" }),
  tracks: z.array(z.string()).min(1, { message: "Please select at least one track" }),
  description: z.string().min(10, { message: "Please provide a brief description of your project" }),
  termsAgreed: z.boolean().refine(val => val === true, { message: "You must agree to the terms and conditions" })
});

type FormValues = z.infer<typeof formSchema>;

export default function License() {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGenre, setSelectedGenre] = useState<string | null>(null);
  
  // Fetch all tracks at once for licensing - force limit to maximum (1000)
  // This is critical for showing all available tracks to the user
  const { data, isLoading: tracksLoading, error } = useQuery<{ 
    tracks: Track[],
    pagination: {
      total: number,
      page: number,
      limit: number,
      totalPages: number
    }
  }>({ 
    queryKey: ['/api/tracks?limit=1000'], // Pass limit directly in URL to ensure it's sent to the API
  });
  
  useEffect(() => {
    if (error) {
      toast({
        title: "Error Loading Tracks",
        description: "There was a problem loading the track catalog. Please try again.",
        variant: "destructive",
      });
      console.error("Track loading error:", error);
    }
  }, [error, toast]);
  
  const allTracks = data?.tracks || [];
  
  // Filter tracks based on search query and genre
  const filteredTracks = useMemo(() => {
    return allTracks.filter(track => {
      const matchesSearch = searchQuery === "" || 
        track.title.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesGenre = selectedGenre === null || track.genre === selectedGenre;
      
      return matchesSearch && matchesGenre;
    });
  }, [allTracks, searchQuery, selectedGenre]);
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      projectType: "",
      usageDuration: "",
      tracks: [],
      description: "",
      termsAgreed: false
    }
  });
  
  const createLicenseRequest = useMutation({
    mutationFn: async (data: FormValues) => {
      setIsSubmitting(true);
      const response = await apiRequest("POST", "/api/license-requests", {
        name: data.name,
        email: data.email,
        projectType: data.projectType,
        usageDuration: data.usageDuration,
        tracks: data.tracks,
        description: data.description
      });
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "License Request Submitted",
        description: "We've received your license request and will review it shortly.",
      });
      setSubmitted(true);
      setIsSubmitting(false);
    },
    onError: (error) => {
      toast({
        title: "Error Submitting Request",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  });
  
  const onSubmit = (data: FormValues) => {
    createLicenseRequest.mutate(data);
  };

  if (submitted) {
    return (
      <div className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-md p-8 text-center">
            <div className="mb-6">
              <div className="w-16 h-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h1 className="text-2xl font-bold mb-4">License Request Received</h1>
            <p className="text-gray-600 mb-6">
              Thank you for your license request. Our team will review it and get back to you shortly. 
              You'll receive a confirmation email with more details.
            </p>
            <div className="flex justify-center">
              <Button onClick={() => window.location.href = "/"}>
                Return to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <div className="mb-10 text-center">
            <h1 className="text-3xl font-bold mb-4 text-foreground">License Music for Your Project</h1>
            <p className="text-muted-foreground">
              Fill out the form below to request a license for your project. We'll review your request 
              and get back to you with a custom quote.
            </p>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="form-card rounded-xl p-6 md:p-8 border-secondary">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-foreground">Your Name</FormLabel>
                      <FormControl>
                        <Input placeholder="John Doe" {...field} className="bg-background text-foreground border-border" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-foreground">Email Address</FormLabel>
                      <FormControl>
                        <Input placeholder="your@email.com" {...field} className="bg-background text-foreground border-border" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <FormField
                  control={form.control}
                  name="projectType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-foreground">Project Type</FormLabel>
                      <Select 
                        onValueChange={field.onChange} 
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="bg-background text-foreground border-border">
                            <SelectValue placeholder="Select a project type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-card text-foreground border-border">
                          <SelectItem value="social">Social Media Video</SelectItem>
                          <SelectItem value="youtube">YouTube Channel</SelectItem>
                          <SelectItem value="tv">TV Show</SelectItem>
                          <SelectItem value="film">Film/Movie</SelectItem>
                          <SelectItem value="podcast">Podcast</SelectItem>
                          <SelectItem value="corporate">Corporate Video</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="usageDuration"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-foreground">Usage Duration</FormLabel>
                      <Select 
                        onValueChange={field.onChange} 
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="bg-background text-foreground border-border">
                            <SelectValue placeholder="Select duration" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-card text-foreground border-border">
                          <SelectItem value="one-time">One-time use</SelectItem>
                          <SelectItem value="six-months">6 months</SelectItem>
                          <SelectItem value="one-year">1 year</SelectItem>
                          <SelectItem value="perpetual">Perpetual</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="tracks"
                render={({ field }) => (
                  <FormItem className="mb-6">
                    <FormLabel className="text-foreground">Select Tracks ({allTracks.length} available)</FormLabel>
                    
                    <div className="mb-4 flex flex-col space-y-2 md:flex-row md:space-y-0 md:space-x-4">
                      <div className="relative flex-1">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search tracks by title..."
                          className="pl-8 bg-background text-foreground border-border"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </div>
                      <Select
                        onValueChange={(value) => setSelectedGenre(value === "all" ? null : value)}
                        value={selectedGenre || "all"}
                      >
                        <SelectTrigger className="w-full md:w-[200px] bg-background text-foreground border-border">
                          <SelectValue placeholder="Filter by genre" />
                        </SelectTrigger>
                        <SelectContent className="bg-card text-foreground border-border">
                          <SelectItem value="all">All Genres</SelectItem>
                          {Array.from(new Set(allTracks.map(t => t.genre))).sort().map(genre => (
                            <SelectItem key={genre} value={genre}>{genre}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <FormControl>
                      <div className="max-h-[300px] overflow-y-auto pr-2 rounded-md border border-border bg-background p-2">
                        <div className="space-y-2 text-foreground">
                          {tracksLoading ? (
                            <div className="flex justify-center py-4">
                              <Loader2 className="h-6 w-6 animate-spin text-primary" />
                            </div>
                          ) : filteredTracks.length === 0 ? (
                            <div className="py-8 text-center text-muted-foreground">
                              <p>No tracks found matching your search criteria</p>
                            </div>
                          ) : (
                            <>
                              <div className="pb-2 text-xs text-muted-foreground">
                                Showing {filteredTracks.length} of {allTracks.length} tracks
                              </div>
                              {filteredTracks.map(track => (
                                <div key={track.id} className="flex items-center space-x-2 py-1 border-b border-border last:border-0">
                                  <Checkbox 
                                    id={`track-${track.id}`}
                                    checked={field.value.includes(track.title)}
                                    onCheckedChange={(checked) => {
                                      if (checked) {
                                        field.onChange([...field.value, track.title]);
                                      } else {
                                        field.onChange(field.value.filter(value => value !== track.title));
                                      }
                                    }}
                                    className="border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                                  />
                                  <label 
                                    htmlFor={`track-${track.id}`}
                                    className="flex-1 cursor-pointer text-sm font-medium leading-none text-foreground peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                  >
                                    <div>{track.title}</div>
                                    <div className="mt-1 text-xs text-muted-foreground">
                                      {track.genre} • {track.bpm} BPM • {track.duration}
                                    </div>
                                  </label>
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                      </div>
                    </FormControl>
                    
                    {field.value.length > 0 && (
                      <div className="mt-2 text-sm text-primary">
                        {field.value.length} track{field.value.length !== 1 ? 's' : ''} selected
                      </div>
                    )}
                    
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem className="mb-6">
                    <FormLabel className="text-foreground">Project Description</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Tell us about your project and how you plan to use our music..." 
                        rows={4}
                        {...field}
                        className="bg-background text-foreground border-border"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="termsAgreed"
                render={({ field }) => (
                  <FormItem className="mb-6">
                    <div className="flex items-start space-x-2">
                      <FormControl>
                        <Checkbox 
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          className="border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="text-foreground">
                          I agree to the terms and conditions and understand that my request will be reviewed before a license is granted.
                        </FormLabel>
                        <FormMessage />
                      </div>
                    </div>
                  </FormItem>
                )}
              />

              <div className="flex justify-center">
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="bg-primary hover:bg-primary/90 text-black font-medium px-8 py-3"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    "Submit License Request"
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
}
